/**
 * Style Guide Page
 *
 * Muestrario interno del design system (paleta, tipografía, componentes).
 * Sirve como QA visual del rebrand y como referencia para nuevos desarrollos.
 *
 * Acceso: /app/style-guide (ruta interna, no expuesta en el menú).
 */

import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { Badge } from '../../design-system/components/Badge';
import { Button } from '../../design-system/components/Button';
import { Card, CardBody, CardHeader } from '../../design-system/components/Card';
import { EmptyState } from '../../design-system/components/EmptyState';
import { Input } from '../../design-system/components/Input';
import { tokens } from '../../design-system/tokens';

export function StyleGuidePage() {
  const [demoValue, setDemoValue] = useState('');

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px 64px' }}>
      <header style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: tokens.semantic.textMuted,
            textTransform: 'uppercase',
          }}
        >
          Design system · Virtual Agents
        </div>
        <h1
          style={{
            margin: '4px 0 6px',
            fontSize: 32,
            fontWeight: 800,
            color: tokens.semantic.textPrimary,
            letterSpacing: '-0.03em',
          }}
        >
          Style Guide
        </h1>
        <p style={{ margin: 0, fontSize: 15, color: tokens.semantic.textMuted, maxWidth: 640 }}>
          Muestrario interno de la paleta, tipografía y componentes después del rebrand. Usar como referencia
          rápida durante el desarrollo.
        </p>
      </header>

      <Section title="Paleta de marca">
        <SwatchRow label="Accent (violeta · acciones principales)" colors={[
          { name: 'primary 50', value: tokens.colors.primary[50] },
          { name: 'primary 100', value: tokens.colors.primary[100] },
          { name: 'primary 500', value: tokens.colors.primary[500] },
          { name: 'primary 600', value: tokens.colors.primary[600] },
          { name: 'primary 700', value: tokens.colors.primary[700] },
        ]} />
        <SwatchRow label="Brand (verde lima · CTA)" colors={[
          { name: 'brand 50', value: tokens.colors.brand[50] },
          { name: 'brand 100', value: tokens.colors.brand[100] },
          { name: 'brand 500', value: tokens.colors.brand[500] },
          { name: 'brand 600', value: tokens.colors.brand[600] },
          { name: 'brand 700', value: tokens.colors.brand[700] },
        ]} />
        <SwatchRow label="Navy (shell / sidebar)" colors={[
          { name: 'navy 500', value: tokens.colors.navy[500] },
          { name: 'navy 600', value: tokens.colors.navy[600] },
          { name: 'navy 700', value: tokens.colors.navy[700] },
          { name: 'navy 800', value: tokens.colors.navy[800] },
          { name: 'navy 900', value: tokens.colors.navy[900] },
          { name: 'navy 950', value: tokens.colors.navy[950] },
        ]} />
        <SwatchRow label="Neutral (texto, bordes, fondos)" colors={[
          { name: 'neutral 50', value: tokens.colors.neutral[50] },
          { name: 'neutral 100', value: tokens.colors.neutral[100] },
          { name: 'neutral 300', value: tokens.colors.neutral[300] },
          { name: 'neutral 500', value: tokens.colors.neutral[500] },
          { name: 'neutral 700', value: tokens.colors.neutral[700] },
          { name: 'neutral 900', value: tokens.colors.neutral[900] },
        ]} />
        <SwatchRow label="Semánticos" colors={[
          { name: 'success', value: tokens.colors.success.main },
          { name: 'warning', value: tokens.colors.warning.main },
          { name: 'error', value: tokens.colors.error.main },
          { name: 'info', value: tokens.colors.info.main },
        ]} />
      </Section>

      <Section title="Tipografía">
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', color: tokens.semantic.textPrimary }}>
              Heading 1 · 30px · 800
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: tokens.semantic.textPrimary }}>
              Heading 2 · 24px · 700
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: tokens.semantic.textPrimary }}>
              Heading 3 · 18px · 600
            </div>
            <div style={{ fontSize: 14, color: tokens.semantic.textSecondary }}>
              Body 14px · 400 — Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </div>
            <div style={{ fontSize: 12, color: tokens.semantic.textMuted }}>
              Muted 12px · 400 — Texto auxiliar de baja jerarquía.
            </div>
          </div>
        </Card>
      </Section>

      <Section title="Botones">
        <Card>
          <CardHeader title="Variantes" subtitle="Tamaños sm / md / lg" />
          <CardBody>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <Button variant="primary">Primary</Button>
              <Button variant="cta">CTA verde</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger">Danger</Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
              <Button variant="cta" size="sm">CTA sm</Button>
              <Button variant="cta">CTA md</Button>
              <Button variant="cta" size="lg">CTA lg</Button>
              <Button variant="primary" loading>
                Loading
              </Button>
              <Button variant="primary" disabled>
                Disabled
              </Button>
            </div>
          </CardBody>
        </Card>
      </Section>

      <Section title="Badges">
        <Card>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Badge variant="neutral">Neutral</Badge>
            <Badge variant="accent" dot>
              New
            </Badge>
            <Badge variant="success">Conectado</Badge>
            <Badge variant="warning">Pendiente</Badge>
            <Badge variant="error">Falló</Badge>
            <Badge variant="info" dot>
              Info
            </Badge>
          </div>
        </Card>
      </Section>

      <Section title="Cards">
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          <Card variant="elevated" interactive onClick={() => {}}>
            <CardHeader title="Elevated" subtitle="Sombra accent al hover" />
            <CardBody>Hace hover para verlo en acción.</CardBody>
          </Card>
          <Card variant="outlined">
            <CardHeader title="Outlined" subtitle="Solo borde" />
            <CardBody>Para agrupar contenido sin demasiado peso visual.</CardBody>
          </Card>
          <Card variant="filled">
            <CardHeader title="Filled" subtitle="Fondo neutral" />
            <CardBody>Para destacar dentro de cards anidados.</CardBody>
          </Card>
        </div>
      </Section>

      <Section title="Formularios">
        <Card>
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <Input
              label="Email"
              type="email"
              placeholder="tu@empresa.com"
              value={demoValue}
              onChange={(e) => setDemoValue(e.target.value)}
              helperText="Te enviaremos la confirmación acá."
            />
            <Input label="Con error" defaultValue="abc" error="Debe contener al menos 8 caracteres." />
            <Input label="Disabled" defaultValue="hola" disabled />
          </div>
        </Card>
      </Section>

      <Section title="EmptyState">
        <EmptyState
          title="Conectá tu primer canal"
          description="Sumá WhatsApp, Instagram o Messenger para empezar a recibir conversaciones."
          primaryAction={{ label: 'Conectar un canal', onClick: () => {} }}
          secondaryAction={{ label: 'Ver guía', href: '#' }}
        />
      </Section>

      <Section title="Shell (sidebar gradient)">
        <div
          style={{
            height: 120,
            borderRadius: 12,
            background: 'var(--va-shell-gradient)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            border: `1px solid ${tokens.semantic.border}`,
          }}
        >
          var(--va-shell-gradient)
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <h2
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: tokens.semantic.textMuted,
          margin: '0 0 12px',
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function SwatchRow({
  label,
  colors,
}: {
  label: string;
  colors: { name: string; value: string }[];
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          fontSize: 12,
          color: tokens.semantic.textSecondary,
          marginBottom: 8,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {colors.map((c) => (
          <div key={c.name} style={swatchStyles}>
            <div
              style={{
                width: '100%',
                height: 48,
                background: c.value,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <div style={{ padding: '6px 8px' }}>
              <div style={{ fontSize: 11, color: tokens.semantic.textMuted }}>{c.name}</div>
              <div style={{ fontSize: 11, fontFamily: tokens.typography.fontFamily.mono, color: tokens.semantic.textPrimary }}>
                {c.value}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const swatchStyles: CSSProperties = {
  width: 116,
  borderRadius: 8,
  border: `1px solid ${tokens.semantic.border}`,
  background: tokens.semantic.surface,
  overflow: 'hidden',
};

export default StyleGuidePage;
